/**
 * DS-03 Apodictic register (INV-CH-DS-03; kept, sentence counts from the new splitter C-SPL).
 *
 * Events per prose sentence: absolute adverbs ×1 (never, always, inevitably, …), modal-passive
 * necessity ×1.6 ("must be cultivated"), maxim-shaped sentences ×1.3 (≤ 12 whitespace-separated
 * words containing a gnomic absolute). Absolutes and modal-passives are matched per prose block,
 * maxims per sentence.
 */
import {
	compiledEnglish,
	fmt,
	line,
	nativeMeasure,
	proseBlocks,
	quote,
	rateDef,
	sentenceExposure,
	sentenceRange,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import { proseSentenceList } from './helpers.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const W_ABS = 1;
const W_MODPASS = 1.6;
const W_MAXIM = 1.3;
const MAXIM_MAX_WORDS = 12;

export const ds03Def = rateDef({
	id: 'DS-03',
	name: 'Apodictic register',
	family: 'RHETORIC',
	features: [
		{ id: 'absolute', label: 'Absolute adverb' },
		{ id: 'modalPassive', label: 'Modal-passive necessity' },
		{ id: 'maxim', label: 'Maxim-shaped sentence' },
	],
	oneWay: true,
	minExposure: 15,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: [
		[0.05, 12],
		[0.15, 30],
		[0.3, 52],
		[0.5, 70],
		[0.8, 86],
		[1.2, 94],
	],
	unitScale: 1,
	unitLabel: 'apodictic units per sentence',
	phi0: 1.5,
	weight: 1.3,
	nd: 0.15,
	changes: ['C-SPL'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const nr = lex.channels['ch-ds03'];
	const iAbs = nr.index.absoluteAdverbs as number;
	const iMod = nr.index.modalPassive as number;
	const maximRe = nr.get('maximShape');
	const k = new Float64Array(doc.paragraphs.count);
	const e = sentenceExposure(doc);
	const ev = makeEvidence();
	const modal: string[] = [];
	let abs = 0;
	let maxims = 0;
	const sl = proseSentenceList(doc);
	for (const b of proseBlocks(doc)) {
		nr.list.scanBlock(b.text, b.base, (s, en, i, m) => {
			if (i === iAbs) {
				abs++;
				k[b.p] = (k[b.p] as number) + W_ABS;
				ev.add(s, en, 0, W_ABS);
			} else if (i === iMod) {
				modal.push(m[0]);
				k[b.p] = (k[b.p] as number) + W_MODPASS;
				ev.add(s, en, 1, W_MODPASS);
			}
		});
		for (let i = sl.paraFirst[b.p] as number; i < (sl.paraFirst[b.p + 1] as number); i++) {
			if ((sl.words[i] as number) === 0) continue;
			const { start, end } = sentenceRange(doc, i);
			// the seed's word count: whitespace-separated pieces
			if (whitespaceWords(doc.text, start, end) > MAXIM_MAX_WORDS) continue;
			if (!maximRe.test(doc.text.slice(start, end))) continue;
			maxims++;
			k[b.p] = (k[b.p] as number) + W_MAXIM;
			ev.add(start, end, 2, W_MAXIM);
		}
	}
	const summary: string[] = [];
	if (abs)
		summary.push(line(`${abs} absolute adverbs`, 'never / always / inevitably / unmistakably …'));
	for (const m of modal.slice(0, 6)) summary.push(line(quote(m), 'modal-passive necessity'));
	if (maxims)
		summary.push(
			line(
				`${maxims} maxim-shaped sentence${maxims === 1 ? '' : 's'}`,
				'short gnomic pronouncements'
			)
		);
	let sc = 0;
	for (let p = 0; p < e.length; p++) sc += e[p] as number;
	return {
		k,
		e,
		evidence: ev,
		summary,
		features: {
			perSentence: (abs * W_ABS + modal.length * W_MODPASS + maxims * W_MAXIM) / Math.max(1, sc),
			absolutes: abs,
			modalPassives: modal.length,
			maxims: Number(fmt(maxims, 0)),
		},
	};
}

/** Whitespace-separated pieces of text[start, end) (the seed's `s.split(/\s+/).length`, edges trimmed). */
function whitespaceWords(text: string, start: number, end: number): number {
	let n = 0;
	let inWord = false;
	for (let i = start; i < end; i++) {
		const c = text.charCodeAt(i);
		const space =
			c === 32 ||
			(c >= 9 && c <= 13) ||
			c === 0xa0 ||
			c === 0x2028 ||
			c === 0x2029 ||
			c === 0xfeff ||
			(c >= 0x2000 && c <= 0x200a) ||
			c === 0x1680 ||
			c === 0x202f ||
			c === 0x205f ||
			c === 0x3000;
		if (space) inWord = false;
		else if (!inWord) {
			inWord = true;
			n++;
		}
	}
	return n;
}

export const ds03: ChannelImpl = rateChannel(ds03Def, nativeMeasure(measure));
export { ds03Def as def, ds03 as impl };
